import { TaskName } from '@/common/constants/taskNames.constants';
import { TaskState } from '@schema';
import i18n from '@/common/i18n';
import type { TaskMap } from '@/common/store/modules/tasks/tasksNotificationMapper';
import { useFileStorageSharesStore } from '@/common/store/pinia/fileStorageShares';
import { store } from '@/common/root';

function getShareName(id) {
  if (!id) return '';
  return useFileStorageSharesStore().getItemById(id)?.name ?? '';
}

function getNodeName(nodes) {
  if (!nodes || nodes.length === 0) return '';

  const node = store.getters['locations/getNodeById'](nodes[0].node_id);
  return node ? node.host : '';
}

const taskMap: TaskMap = {
  [TaskName.FILE_STORAGE_CLUSTER_CREATE]: {
    [TaskState.SUCCESS]: () => ({
      title: i18n.t('File storage created'),
      message: i18n.t('Configured and ready to use')
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Creating file storage...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Failed to create file storage'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_CLUSTER_DELETE]: {
    [TaskState.SUCCESS]: () => ({
      title: i18n.t('File storage successfully deleted'),
      message: i18n.t('File storage')
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Deleting file storage...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error deleting file storage'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_NODE_ADD]: {
    [TaskState.SUCCESS]: ({ payload }) => {
      const count = payload?.nodes?.length || 1;
      return {
        title: i18n.tc('Node added|Nodes added', count),
        message: i18n.tc('Node "{name}" added to file storage|{count} nodes added to file storage', count, { name: getNodeName(payload?.nodes) })
      };
    },
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Adding node to file storage...')
    }),
    [TaskState.FAILED]: ({ error, payload }) => {
      const count = payload?.nodes?.length;
      return {
        title: i18n.tc('Failed to add node|Failed to add {count} nodes', count, { count }),
        message: error
      };
    }
  },
  [TaskName.FILE_STORAGE_NODE_DELETE]: {
    [TaskState.SUCCESS]: ({ payload }) => {
      const count = payload?.nodes?.length || 1;
      return {
        title: i18n.tc('Node released|Nodes released', count),
        message: i18n.tc('Node "{name}" released|{count} nodes released', count, { name: getNodeName(payload?.nodes) })
      };
    },
    [TaskState.RUNNING]: ({ payload }) => {
      const count = payload?.nodes?.length || 1;
      return {
        message: i18n.tc('Removing node from file storage...|Removing {count} nodes from file storage...', count, { count })
      };
    },
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Error releasing node'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_SHARE_CREATE]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('Share created'),
      message: payload?.name,
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Creating share...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Failed to create share'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_SHARE_UPDATE]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('Share updated'),
      message: getShareName(payload?.share_id),
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Updating share...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Failed to update share'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_SHARE_STOP]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('Share stopped'),
      message: getShareName(payload?.share_id),
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Stopping share...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Failed to stop share'),
      message: error
    })
  },
  [TaskName.FILE_STORAGE_SHARE_START]: {
    [TaskState.SUCCESS]: ({ payload }) => ({
      title: i18n.t('Share started'),
      message: getShareName(payload?.share_id),
    }),
    [TaskState.RUNNING]: () => ({
      message: i18n.t('Starting share...')
    }),
    [TaskState.FAILED]: ({ error }) => ({
      title: i18n.t('Failed to start share'),
      message: error
    })
  }
};

export default taskMap;
